import { useEffect, useMemo, useState, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Archive, Download, Search, FileText, MessageCircle, AlertTriangle, RefreshCw, X, ChevronDown, BarChart3, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import WeightedPricesChart from '@/components/WeightedPricesChart';

const ANALYTICS_OWNER_EMAIL = 'supply@chemdel.net';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('ru-RU'); } catch { return iso; }
};

export default function ArchivePanel() {
  const { user } = useAuth();
  const isAnalyticsOwner = (user?.email || '').toLowerCase() === ANALYTICS_OWNER_EMAIL;
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [tenders, setTenders] = useState([]);
  const [products, setProducts] = useState([]);
  const [productOpen, setProductOpen] = useState(false);
  const [productQuery, setProductQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | active | archived
  const [search, setSearch] = useState('');
  const [productName, setProductName] = useState('');
  const [bidEndFrom, setBidEndFrom] = useState('');
  const [bidEndTo, setBidEndTo] = useState('');
  const [onlyWithWinner, setOnlyWithWinner] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState('chronological'); // chronological | summary
  const [includeChat, setIncludeChat] = useState(true);
  const [includeBids, setIncludeBids] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        include_active: filter !== 'archived',
        include_archived: filter !== 'active',
      };
      if (search) params.search = search;
      if (productName) params.product_name = productName;
      if (bidEndFrom) params.bid_end_from = bidEndFrom;
      if (bidEndTo) params.bid_end_to = bidEndTo;
      if (onlyWithWinner) params.only_with_winner = true;
      const { data } = await api.get('/admin/archive/tenders', { params });
      setTenders(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Не удалось загрузить архив');
    } finally {
      setLoading(false);
    }
  };

  // Серверные фильтры применяются автоматически при их изменении.
  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [filter, productName, bidEndFrom, bidEndTo, onlyWithWinner]);

  // Один раз грузим справочник товаров для дропдауна.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/products');
        setProducts(data || []);
      } catch {/* справочник опциональный, не блокируем */}
    })();
  }, []);

  // Закрытие дропдауна по клику вне.
  const productBoxRef = useRef(null);
  useEffect(() => {
    if (!productOpen) return;
    const handler = (e) => {
      if (productBoxRef.current && !productBoxRef.current.contains(e.target)) {
        setProductOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [productOpen]);

  const filteredProducts = useMemo(() => {
    const q = (productQuery || '').toLowerCase().trim();
    if (!q) return products.slice(0, 50);
    return products
      .filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.name_en || '').toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [products, productQuery]);

  const resetFilters = () => {
    setSearch('');
    setProductName('');
    setProductQuery('');
    setBidEndFrom('');
    setBidEndTo('');
    setOnlyWithWinner(false);
    setFilter('all');
  };

  const filteredTenders = useMemo(() => {
    if (!search) return tenders;
    const q = search.toLowerCase();
    return tenders.filter(t =>
      (t.lot_name || '').toLowerCase().includes(q) ||
      String(t.lot_number || '').includes(q) ||
      (t.product_name || '').toLowerCase().includes(q) ||
      (t.organizer_company || '').toLowerCase().includes(q)
    );
  }, [tenders, search]);

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const allSelected = filteredTenders.length > 0 && filteredTenders.every(t => selected.has(t.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filteredTenders.map(t => t.id)));
  };

  const exportDocx = async (allMode = false) => {
    const ids = allMode ? null : Array.from(selected);
    if (!allMode && ids.length === 0) {
      toast.warning('Выберите хотя бы один тендер или нажмите «Экспорт всех»');
      return;
    }
    setExporting(true);
    try {
      const res = await api.post('/admin/archive/export-word',
        {
          tender_ids: ids,
          mode,
          include_chat: includeChat,
          include_bids: includeBids,
        },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `basequote_archive_${ts}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(allMode ? 'Экспорт всех тендеров готов' : `Экспортировано: ${ids.length} тендер(ов)`);
    } catch (e) {
      const blob = e?.response?.data;
      let msg = 'Ошибка экспорта';
      if (blob && blob.text) {
        try { msg = JSON.parse(await blob.text()).detail || msg; } catch {}
      }
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const archivedCount = tenders.filter(t => t.is_archived).length;
  const activeCount = tenders.length - archivedCount;

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="archive-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-amber-100 flex items-center justify-center">
            <Archive className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Архив тендеров</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Полный доступ ко всем активным и удалённым тендерам с переписками. Только для супер-админа.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="archive-refresh"
          className="rounded-sm border-slate-300 text-slate-600 hover:bg-slate-50">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Обновить
        </Button>
      </div>

      {/* Toolbar */}
      <div className="px-6 py-3 border-b border-slate-200 bg-slate-50/40">
        <div className="grid md:grid-cols-2 gap-3 items-end">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <label className="text-xs text-slate-600">Поиск</label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Лот, номер, товар, организатор..." className="pl-7 h-8 text-xs rounded-sm border-slate-300"
                  data-testid="archive-search" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-600">Фильтр</label>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="h-8 text-xs w-32 rounded-sm border-slate-300" data-testid="archive-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="active">Активные</SelectItem>
                  <SelectItem value="archived">Архивные</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-end gap-2 justify-end flex-wrap">
            <div className="space-y-1">
              <label className="text-xs text-slate-600">Формат</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-8 text-xs w-44 rounded-sm border-slate-300" data-testid="archive-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chronological">Хронологический</SelectItem>
                  <SelectItem value="summary">Сводка</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="text-[11px] text-slate-600 flex items-center gap-1.5 pb-1.5">
              <Checkbox checked={includeChat} onCheckedChange={setIncludeChat} data-testid="archive-include-chat" />
              Чат
            </label>
            <label className="text-[11px] text-slate-600 flex items-center gap-1.5 pb-1.5">
              <Checkbox checked={includeBids} onCheckedChange={setIncludeBids} data-testid="archive-include-bids" />
              Ставки
            </label>
            <Button onClick={() => exportDocx(false)} disabled={exporting || selected.size === 0}
              data-testid="archive-export-selected" size="sm"
              className="h-8 px-3 bg-[#0046AD] hover:bg-[#003388] text-white rounded-sm">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Экспорт ({selected.size})
            </Button>
            <Button onClick={() => exportDocx(true)} disabled={exporting}
              data-testid="archive-export-all" size="sm" variant="outline"
              className="h-8 px-3 border-slate-300 text-slate-700 rounded-sm">
              Экспорт всех
            </Button>
          </div>
        </div>

        <div className="mt-3 grid md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1" ref={productBoxRef}>
            <label className="text-xs text-slate-600">Закупаемый товар</label>
            <div className="relative">
              <div className="flex items-center h-8 rounded-sm border border-slate-300 bg-white px-2 cursor-text"
                onClick={() => setProductOpen(true)}>
                <Input value={productOpen ? productQuery : (productName || '')}
                  onChange={(e) => { setProductQuery(e.target.value); setProductOpen(true); }}
                  onFocus={() => { setProductOpen(true); setProductQuery(''); }}
                  placeholder="Выберите товар…"
                  className="h-7 text-xs border-0 p-0 focus-visible:ring-0 shadow-none"
                  data-testid="archive-product-name" />
                {productName && !productOpen && (
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setProductName(''); setProductQuery(''); }}
                    className="text-slate-400 hover:text-slate-700 mr-1"
                    data-testid="archive-product-clear"
                    title="Очистить">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </div>
              {productOpen && (
                <div className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg"
                  data-testid="archive-product-dropdown">
                  {filteredProducts.length === 0 ? (
                    <div className="px-3 py-2 text-[11px] text-slate-400">
                      Ничего не найдено в справочнике
                    </div>
                  ) : filteredProducts.map(p => (
                    <button key={p.id || p.name}
                      type="button"
                      onClick={() => {
                        setProductName(p.name || '');
                        setProductQuery('');
                        setProductOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${productName === p.name ? 'bg-blue-50/60' : ''}`}
                      data-testid={`archive-product-option-${p.name}`}>
                      <div className="font-medium text-slate-800 truncate">{p.name}</div>
                      {p.name_en && p.name_en !== p.name && (
                        <div className="text-[10px] text-slate-400 truncate">{p.name_en}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-600">Конец торгов с</label>
            <Input type="date" value={bidEndFrom}
              onChange={(e) => setBidEndFrom(e.target.value)}
              className="h-8 text-xs rounded-sm border-slate-300"
              data-testid="archive-bid-end-from" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-600">Конец торгов по</label>
            <Input type="date" value={bidEndTo}
              onChange={(e) => setBidEndTo(e.target.value)}
              className="h-8 text-xs rounded-sm border-slate-300"
              data-testid="archive-bid-end-to" />
          </div>
          <div className="flex items-center gap-2 pb-1">
            <label className="text-[11px] text-slate-700 flex items-center gap-1.5 cursor-pointer">
              <Checkbox checked={onlyWithWinner}
                onCheckedChange={setOnlyWithWinner}
                data-testid="archive-only-with-winner" />
              <span title="Только завершённые тендеры со ставками — исключает черновики, отменённые и принудительно завершённые без ставок">
                Только с победителем
              </span>
            </label>
            <Button variant="ghost" size="sm" onClick={resetFilters}
              data-testid="archive-reset-filters"
              className="h-7 text-[11px] text-slate-500 hover:text-slate-700 ml-auto">
              Сбросить
            </Button>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
          <span>Всего: <span className="font-semibold text-slate-700">{tenders.length}</span></span>
          <span>Активных: <span className="font-semibold text-slate-700">{activeCount}</span></span>
          <span>Архивных: <span className="font-semibold text-amber-700">{archivedCount}</span></span>
        </div>
      </div>

      {/* Аналитика средневзвешенных цен — доступна только владельцу аналитики */}
      {isAnalyticsOwner && (
        <div className="px-6 py-3 border-b border-slate-200 bg-blue-50/30">
          <label className="text-xs text-slate-700 flex items-center gap-2 cursor-pointer select-none"
            data-testid="analytics-toggle-label">
            <Checkbox checked={showAnalytics} onCheckedChange={setShowAnalytics}
              data-testid="analytics-toggle" />
            <BarChart3 className="w-3.5 h-3.5 text-blue-700" />
            <span className="font-medium">Показать аналитику средневзвешенных цен</span>
          </label>
          {showAnalytics && (
            <WeightedPricesChart
              productName={productName}
              bidEndFrom={bidEndFrom}
              bidEndTo={bidEndTo}
            />
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="archive-toggle-all" />
              </TableHead>
              <TableHead>Лот</TableHead>
              <TableHead>Организатор</TableHead>
              <TableHead className="text-center">Статус</TableHead>
              <TableHead className="text-right">Ставки</TableHead>
              <TableHead className="text-right">Чат</TableHead>
              <TableHead>Создан</TableHead>
              <TableHead>Архивирован</TableHead>
              <TableHead className="text-right w-24">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center text-xs text-slate-400 py-6">Загрузка...</TableCell></TableRow>
            ) : filteredTenders.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center text-xs text-slate-400 py-6">Тендеры не найдены</TableCell></TableRow>
            ) : filteredTenders.map(t => (
              <TableRow key={t.id} className={t.is_archived ? 'bg-amber-50/30' : ''}
                data-testid={`archive-row-${t.id}`}>
                <TableCell>
                  <Checkbox checked={selected.has(t.id)} onCheckedChange={() => toggle(t.id)}
                    data-testid={`archive-check-${t.id}`} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {t.is_archived && <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate max-w-[280px]">{t.lot_name || '—'}</div>
                      <div className="text-[10px] text-slate-400">
                        {t.lot_number ? `№${t.lot_number} · ` : ''}{t.product_name || ''}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  <div className="text-slate-700 truncate max-w-[180px]">{t.organizer_company || '—'}</div>
                  <div className="text-[10px] text-slate-400 truncate">{t.organizer_email || ''}</div>
                </TableCell>
                <TableCell className="text-center">
                  {t.is_archived ? (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] rounded-sm">АРХИВ</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px] rounded-sm">{t.status}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums text-xs">
                  <span className="inline-flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> {t.bid_count || 0}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums text-xs">
                  <span className="inline-flex items-center gap-1 text-slate-600">
                    <MessageCircle className="w-3 h-3" /> {t.chat_count || 0}
                  </span>
                </TableCell>
                <TableCell className="text-[11px] text-slate-500">{fmt(t.created_at)}</TableCell>
                <TableCell className="text-[11px] text-slate-500">
                  {t.is_archived ? (
                    <div>
                      <div>{fmt(t.archived_at)}</div>
                      {t.archived_by && <div className="text-[10px] text-slate-400">{t.archived_by}</div>}
                    </div>
                  ) : '—'}
                </TableCell>
                <TableCell className="text-right">
                  {t.is_archived && isAnalyticsOwner && (
                    <Button size="sm" variant="ghost"
                      onClick={async () => {
                        if (!window.confirm(`Окончательно удалить «${t.lot_name}»?\nЭто действие нельзя отменить — будут стёрты ставки, чат, файлы и сама запись о тендере.`)) return;
                        try {
                          const { data } = await api.delete(`/admin/archive/tenders/${t.id}`);
                          toast.success(`Удалено: «${data.lot_name || t.lot_name}»`);
                          load();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Не удалось удалить');
                        }
                      }}
                      data-testid={`hard-delete-${t.id}`}
                      className="h-7 px-2 text-[11px] text-rose-700 hover:bg-rose-50 hover:text-rose-800">
                      <Trash2 className="w-3 h-3 mr-1" /> Удалить
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
